import React, {
	useEffect,
	useState,
} from 'react'
import {
	useSelector,
	useDispatch
} from 'react-redux'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import Form from 'react-bootstrap/Form'
import Select from "react-select"
import DatePicker from "react-datepicker"
import {
	copyEventToOtherClients,
	selectEventToCopy,
	copyEventToSelf,
	copyAndEdit,
	updateCopy,
} from '../../../state/events'
import {
	selectAllClients
} from '../../users/usersSlice'
import {
	extractEventParams,
	getTrainerParam,
	envAwarePost,
	capitalize,
	dateToYMD
} from '../../../util/util'
import { useHistory } from 'react-router-dom'
import useUsersForEventCopy from '../../../hooks/useUsersForEventCopy'
import useCurrentUser from '../../../hooks/useCurrentUser'


const mapTagsToOptions = tags => tags?.map(t => ({
	value: t.id,
	label: t.name,
	id: t.id
}))

const mapUsersToOptions = users => {
	//map from account type to array of user opptions
	const accountTypeMap = {}
	users.forEach(user => {
		if (!Boolean(accountTypeMap[user.account_type])) {
			accountTypeMap[user.account_type] = []
		}
		accountTypeMap[user.account_type].push({
			value: user.user_id_number,
			label: user.user_name 
		})
	})
	const typeToLabel = type => {
		switch (type) {
			case 'client': return 'All Clients'
			case 'gym': return 'Gym'
			case 'trainer': return 'Other Trainers'
		}
	}
	//turn into format for react select
	const groupedOptions = Object.entries(accountTypeMap).map(([type, options]) => ({
		label: typeToLabel(type),
		options
	}))
	return groupedOptions
}

/**
 * Expected behavior: Opens with data pre-filled from selected event
 * eg: if selected event is a draft the status select should default to draft
 */
const CopyModal = () => {
	const user = useCurrentUser()
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch();
	let history = useHistory();

	const { filter } = extractEventParams(window.location.search)
	const { account_type, user_id_number } = useSelector(state => state.users.current_user)

	const [copyByTag,setCopyByTag] = useState(false)

	const isClient = user.account_type === 'client'

	/* --- selectors --- */

	const { show, id, type, origin, defaultClientSelected } = useSelector(state => state.allEvents.copy)
	const event = useSelector(selectEventToCopy)
	//const clients = useSelector(selectAllClients)
	const { status, users, warning } = useUsersForEventCopy(type, id)

	const accountOptions = mapUsersToOptions(users)

	const calendarClientSelected = useSelector(state => state.allEvents.calendarClientSelected)

	//allow trainers/gym the ability to copy from the gym to their favorites
	//or create copies of a favorite event
	if (account_type !== 'client') accountOptions.unshift({
		label: `My Favorites`,
		value: user_id_number
	})

	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

	/* --- state --- */
	//for bulk copy
	const [clientIds, setClientIds] = useState([])
	//for copy and edit
	const [tgtClientId, setTgtClientId] = useState(null)

	const [tags, setTags] = useState([])
	const [tagsToAssign,setTagsToAssign] = useState([])

	const [clientsSelected, setClientsSelected] = useState([]);
	const [copyEditClient, setCopyEditClient] = useState([]);

	//repeate related state
	const [repeatType, setRepeatType] = useState('none')
	const [dayString, setDayString] = useState(1);
	const [customDayString, setCustomDayString] = useState("");
	const [weekDays, setWeekdays] = useState([])

	const [returnFirstCreated, setReturnFirstCreated] = useState(0)
	//copy || copy-and-edit
	const [copyType, setCopyType] = useState('copy')

	//event related state
	const [note, setNote] = useState('')
	const [date, setDate] = useState(new Date())
	const [endDate, setEndDate] = useState("")
	const [published, setPublished] = useState(0)

	//pending status related
	const [isCopying, setIsCopying] = useState(false)

	// error messaging
	const [dateError, setDateError] = useState(false);
	const [frequencyError, setFrequencyError] = useState(false);
	const [copyToError, setCopyToError] = useState(false);

	const tagsOptions = mapTagsToOptions(tags)

	/* --- effects --- */
		//default selected client
	useEffect(() => {
		if (!show) return;
		accountOptions.forEach(list=>{
			let itemSelected = list?.options?.filter(item =>(
				item.value == defaultClientSelected
			))
			if (itemSelected) {
				// for copy and edit option (single client)
				setCopyEditClient(itemSelected);
				setTgtClientId(itemSelected[0]?.value)

				// for copy option (can be multiple clients)
				setClientsSelected(itemSelected);
				setClientIds([itemSelected[0]?.value])
			}
		})
		// accountOptions used in useEffect is derived from users
	}, [users,show,defaultClientSelected])


	//whenever event get's updated make sure to reset
	//local state
	useEffect(() => {
		if (!event) return
		setNote(event.note)
		setDate(new Date())
		if (event.date) {
			setEndDate(new Date())
		}

		setPublished(event.published)
		//clear out selection
		setClientIds([])
	}, [event])

	useEffect(() => {
		envAwarePost(
			'api/users/api/',
			{ action: 'getAllTags' }
		).then(res => {
			setTags(res.data)
		})
	}, [show])

	/* --- handlers --- */

	const handleClose = () => {
		setClientIds([])
		setTgtClientId(null)
		setRepeatType("none")
		setClientsSelected([])
		dispatch(updateCopy({ show: false }))
	}

	const handleWeekdaySelect = e => {
		setFrequencyError(false);
		const day = e.target.value
		if (weekDays.includes(day)) {
			setWeekdays(weekDays.filter(d => d !== day))
		} else {
			setWeekdays([...weekDays, day])
		}
	}

	const handleCopy = () => {

		let error = false;
		if (copyType === 'copy') {
			if (clientIds.length === 0&&!isClient&&!copyByTag) {
				setCopyToError(true);
				error = true;
			}

			if (repeatType === "daily" || repeatType === "weekly") {
				if (date >= endDate) {
					setDateError(true)
					error = true;
				}
			}

			if (repeatType === 'daily') {
				if (dayString === "custom") {
					if (customDayString < 1) {
						setFrequencyError(true)
						error = true;
					}
				} else if (dayString.length < 1) {
					setFrequencyError(true)
					error = true;
				}


			} else if (repeatType === "weekly") {
				if (weekDays.length < 1) {
					setFrequencyError(true)
					error = true;
				}
			}

			if (error) {
				return
			}

			setIsCopying(true)

			const payload = {
				event_id: id,
				event_type: type,
				additional_client_ids:clientIds,
				copy_by_tag:Number(copyByTag),
				tag_ids:tagsToAssign.map(t=>t.value),
				title: note,
				published,
				start_date: dateToYMD(date),
				end_date: dateToYMD(endDate),
				recurrence_type: repeatType,
				return_first_created: (
					//copy and edit should return the newly created
					//event so we can get it's event_id
					copyType === 'copy' ?
						0 : 1
				)
			}

			if (repeatType === 'daily') {
				if (dayString === "custom") {
					payload.day_string = customDayString;
				} else {
					payload.day_string = dayString;
				}
			} else {
				payload.day_string = weekDays.join(',')
			}

			//clients use a different action
			const action = (
				isClient
				? copyEventToSelf
				: copyEventToOtherClients
			)

			statusDispatch(action(payload), type, "add")
				.then(() => {
					handleClose()
					setIsCopying(false)
					//reset the selected week days 
					setWeekdays([])
				})
		} else {
			if (!tgtClientId) {
				setCopyToError(true);
				return
			}
			setIsCopying(true)

			const urlClient = (
				//if tgt user is the logged in user then make sure we
				//display the correct bread crumb in the editor
				tgtClientId === user_id_number ?
					'favorite' :
					tgtClientId
			)
			dispatch(copyAndEdit({
				event_id: id,
				event_type: type,
				tgt_client_id: tgtClientId
			})).then(action => {
				setIsCopying(false)
				const { data: newEvent } = action.payload
				if (origin === "EventCalendar") {
					history.push(`/eventEditor/?event_id=${newEvent[`${type}_id`]}&event_type=AddOrEdit&eventType=${type}&client=${urlClient}&origin=EventCalendar`)
				} else {
					history.push(`/eventEditor/?event_id=${newEvent[`${type}_id`]}&event_type=AddOrEdit&eventType=${type}&client=${urlClient}&origin=copyEdit`)
				}
			})
		}
	}

	const handleCopyAndEditToSelf = () => {
		
		setIsCopying(true)

		dispatch(copyAndEdit({
			event_id: id,
			event_type: type,
			title: note,
			start_date: dateToYMD(date),
			end_date: dateToYMD(endDate)
		})).then(action => {
			setIsCopying(false)
			const { data: newEvent } = action.payload
			history.push(`/eventEditor/?event_id=${newEvent[`${type}_id`]}&event_type=AddOrEdit&eventType=${type}&client=${user_id_number}&origin=copyEdit`)
		})
	}

	function copyToFavorites() {
		let favorites = [];
		favorites = clientIds.filter(item => item == user_id_number)
		return favorites.length > 0;

	}

	const ReadableRepeat = ({ repeatType, eventType, startDate, endDate, repeatData }) => {
		if (!repeatType || !eventType || !startDate || !endDate || !repeatData.length) return ''
		const repeatString = () => {
			if (repeatType === 'daily') {
				if (repeatData === "2x" || repeatData === "3x") {
					return `${repeatData} per day`
				}
				else if (parseInt(repeatData) === 1) {
					return 'every day'
				} else {
					return `every ${repeatData} days`
				}
			} else {
				return repeatData.join(', ')
			}
		}
		const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }

		return (
			<small className="mt-2 mb-3 d-block text-center">
				This {eventType} will repeat starting on
				<span className="mx-1 text-primary">{startDate.toLocaleDateString(undefined, dateOptions)}</span>
				and repeat
				<span className="mx-1 text-primary">{repeatString()}</span>
				until
				<span className="mx-1 text-primary">{endDate.toLocaleDateString(undefined, dateOptions)}</span>
			</small>
		)
	}

	if (!event) return null
	return (
		<Modal show={show} onHide={handleClose}>
			<Modal.Header closeButton className="d-flex align-items-center">
				<strong>Copy Options</strong>
			</Modal.Header>
			<Modal.Body>
				{!isClient&&
					<Button 
						onClick={()=>{setCopyType(prev=>prev==='copy'?'copy-and-edit':'copy')}}
						variant="outline-primary"
						className="mb-1"
						size="sm"
						block
					>
						{copyType==='copy'?
							'Go To Copy & Edit Options'
							:
							'Go To Bulk Copy Options'
						}
					</Button>
				}
				{copyType==='copy'&&
					<div eventKey="copy" title="Copy">
						{!isClient&&
							<>
								<Form.Label className="mt-2">Copy To:</Form.Label>
								<Select
									value={
										copyByTag
										? tagsToAssign
										: clientsSelected
									}
									onChange={selectedOptions => {
										setCopyToError(false);
										if(copyByTag){
											setTagsToAssign(selectedOptions)
										}else{
											setClientsSelected(selectedOptions)
											setClientIds(
												selectedOptions.map(o => o.value)
											)
										}
									}}
									noOptionsMessage={() => (
										status === 'pending' 
										? `Loading ${copyByTag?'tags':'clients'}...` 
										: `No ${copyByTag?'tags':'clients'}`
									)}
									options={
										copyByTag
										? tagsOptions
										: accountOptions
									}
									isMulti

								/>
								<Form.Check 
									className="mt-1"
									onChange={e=>{
										setCopyByTag(e.target.checked)
									}}
									checked={copyByTag}
									id="copy-workout-by-tag-check"
									label="Copy to clients by tag"
								/>
							</>
						}
						{Boolean(warning) &&
							<div>
								<small className="text-muted">{warning}</small>
							</div>
						}
						{copyToError && <div className="text-danger"> select at least one client</div>}
						<Form.Label className="mt-2">New Title</Form.Label>
						<Form.Control
							onChange={e => setNote(e.target.value)}
							value={note}
							placeholder="new title"
						/>

						{!copyToFavorites() && <>
							{!isClient&&
								<>
									<Form.Label className="mt-2">New Published Status</Form.Label>
									<Form.Control
										onChange={e => setPublished(e.target.value)}
										value={published}
										as="select"
										custom
									>
										<option value={1}>Published</option>
										<option value={0}>Save as draft</option>
									</Form.Control>
								</>
							}

							<Form.Label className="mt-2">Repeat</Form.Label>
							<Form.Control as="select" custom onChange={e => {
								setRepeatType(e.target.value)
								if (e.target.value !== "none") {
									setEndDate("")
								}
								setFrequencyError(false);
							}}>
								<option value="none">None</option>
								<option value="daily">Daily</option>
								<option value="weekly">Weekly</option>
							</Form.Control>
							<Form.Label className="mt-2">{repeatType == 'none' ? 'New Date' : 'Start Date'}</Form.Label>
							<div>
								<DatePicker
									onChange={newDate => {
										setDateError(false)
										setDate(newDate)
									}}
									dateFormat="EEE, MMM d, yyyy"
									selected={date}
									customInput={<Form.Control />}
								/>
							</div>
						</>}
						{repeatType !== 'none' &&
							<>
								<Form.Label className="mt-2">End Date</Form.Label>
								<div>
									<DatePicker
										onChange={newDate => {
											setDateError(false)
											setEndDate(newDate)
										}}
										dateFormat="EEE, MMM d, yyyy"
										selected={endDate}
										customInput={<Form.Control />}
									/>
								</div>
								{dateError && <div>
									<small className="text-danger">
										<i> End Date should be after Start Date.</i>
									</small>
								</div>}

								{
									repeatType === 'daily' ?
										<>
											<Form.Label className="mt-2">Repeat Frequency </Form.Label>
											<Form.Control as={"select"}
												value={dayString}
												onChange={e => setDayString(e.target.value)}
												placeholder="days"
											>
												<option value="3x"> Three Times Per Day </option>
												<option value="2x"> Two Times Per Day </option>
												<option value="1"> Every Day </option>
												<option value="2"> Every 2nd Day </option>
												<option value="3"> Every 3rd Day </option>
												<option value="4"> Every 4th Day </option>
												<option value="5"> Every 5th Day </option>
												<option value="6"> Every 6th Day </option>
												<option value="7"> Every 7th Day </option>
												<option value="8"> Every 8th Day </option>
												<option value="9"> Every 9th Day </option>
												<option value="10"> Every 10th Day </option>
												<option value="custom"> Custom </option>
											</Form.Control>

											{dayString === "custom" &&
												<Form.Group>
													<Form.Label>Custom Repeat Frequency</Form.Label>
													<Form.Control
														type="number"
														onChange={e => {
															setCustomDayString(e.target.value)
															setFrequencyError(false)
														}}
														placeholder="days"
													/>
												</Form.Group>}
											{frequencyError &&
												<div className="text-danger">
													valid repeat frequency required
												</div>
											}
										</>
										:
										<>
											<Form.Label className="mt-2">Choose Days</Form.Label>
											{frequencyError &&
												<div className="mb-2 text-danger">
													select at least one day
												</div>
											}
											{days.map(day => (
												<Form.Check
													key={day}
													value={day}
													onChange={handleWeekdaySelect}
													type="checkbox"
													id={`weekday-${day}`}
													label={day}
												/>
											))}
										</>
								}

								<ReadableRepeat
									repeatType={repeatType}
									eventType={type}
									startDate={date}
									endDate={endDate}
									repeatData={
										repeatType === 'daily' ?
											(customDayString ? customDayString : dayString) :
											weekDays
									}
								/>
							</>
						}
					</div>
				}
				{copyType==='copy-and-edit'&&
					<div eventKey="copy-and-edit" title="Copy & Edit">
						<Form.Label className="mt-2">Copy To:</Form.Label>
						<Select
							value={copyEditClient}
							onChange={selectedOption => {
								setCopyEditClient(selectedOption)
								setTgtClientId(selectedOption.value)
								setCopyToError(false);
							}}
							options={accountOptions}
						/>
						{copyToError && <div className="text-danger"> select one client</div>}

						<small>Choose 1 client to copy to and click "Copy & Go Edit". You will then be able to edit and assign to additional clients.</small>
					</div>
				}
			</Modal.Body>
			<Modal.Footer>
				{isCopying&&<Spinner animation="border" size="sm" />}
				{isClient&&
					<Button disabled={isCopying} variant="primary" onClick={handleCopyAndEditToSelf} size="sm">
						Copy and edit
					</Button>
				}
				<Button disabled={isCopying} variant="primary" onClick={handleCopy} size="sm">
					{
						copyType === 'copy' 
						? (
							isClient
							? "Copy"
							: "Copy to selected accounts"
						)
						: "Copy and edit"
					}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

export default CopyModal